<template lang="pug">
  v-card
    v-card-title
      h2.modal-title {{ 'modals.add_fodder' | translate }}
    v-card-content
      v-type-select(
        v-model="typeSearchText"
        :label="$t('base.ingredient_type')"
        :hint="errorMessage('ingredient_type')"
        :invalid="!!errorMessage('ingredient_type')"
        :custom-types="filteredCustomTypes"
        :default-types="filteredDefaultTypes"
        @select="onTypeSelect"
        @create-enabled="onCreateTypeEnabled"
        @create-new="onCreateNew")
      v-text-field(
        label-active
        :label="$t('base.name')"
        :hint="errorMessage('name')"
        :invalid="!!errorMessage('name')"
        @input="clearError('name')"
        v-model="form.name")
      v-select(
        label-active
        :label="$t('base.stock')"
        returnObject
        :options="filteredStocks"
        :hint="errorMessage('stock')"
        :invalid="!!errorMessage('stock')"
        @input="clearError('stock')"
        v-model="form.stock")
      .toogle-wrap
        ui-toggle-unit(
          :tolerancePercent="form.tolerance_percent"
          :toleranceUnit="form.tolerance_unit"
          :toleranceKg="form.tolerance_kg"
          @update="onToleranceUpdate")
      v-switch.mb-3(
        :label="$t('base.ignore_in_reports')"
        v-model="form.ignored_in_report")
      v-switch.mb-2(
        :label="$t('base.auto_load')"
        v-model="form.auto_load")
    v-card-actions
      v-btn(
        :disabled="!isFormFilled"
        :loading="loading"
        @click="onCreate")
        | {{ 'base.add' | translate }}
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import formErrorsMixin from '@/mixins/form-errors.mixin.js'
import UiToggleUnit from '@/components/ui/ui-toggle-unit/UiToggleUnit.vue'

export default {
  name: 'CreateFodderModal',

  components: {
    UiToggleUnit
  },

  mixins: [formErrorsMixin],

  data: () => ({
    form: {
      name: null,
      ingredient_type: null,
      stock: null,
      dry_matter: 0,
      is_animalfeed: true,
      ignored_in_report: false,
      auto_load: false,
      tolerance_kg: 0,
      tolerance_percent: 97,
      tolerance_unit: 'percent'
    },
    errors: {
      name: null,
      stock: null,
      ingredient_type: null
    },
    loading: false,
    typeSearchText: '',
    selectedType: null,
    isDefaultType: false,
    createTypeEnabled: false,
    creatingNewType: false
  }),

  computed: {
    ...mapGetters('ingredienttypes', ['ingredientTypes', 'defaultIngredientTypes']),
    ...mapGetters('stocks', ['stocks']),

    filteredStocks () {
      return this.stocks
        .filter(item => (item.farm === null && (item.company && item.company.id) === +this.$route.params.company_id) ||
          (item.farm && item.farm.id) === +this.$route.params.farm_id)
    },

    filteredCustomTypes () {
      return this.ingredientTypes.filter(type => !type.deleted)
    },

    filteredDefaultTypes () {
      const customNames = this.ingredientTypes
        .filter(type => !type.deleted)
        .map(t => t.name.toLowerCase())

      return this.defaultIngredientTypes.filter(defaultType =>
        !customNames.includes(defaultType.name.toLowerCase())
      )
    },

    isFormFilled () {
      return !!this.form.name && !!this.form.stock && !!this.typeSearchText
    }
  },

  async mounted () {
    const farm = +this.$route.params.farm_id
    await this.loadIngredientTypes({ farm, limit: 10000 })
    await this.loadDefaultIngredientTypes()

    const company = +this.$route.params.company_id
    this.loadStocks({ company, farm })
  },

  methods: {
    ...mapActions('ingredients', ['createIngredient', 'loadIngredients']),
    ...mapActions('ingredienttypes', ['loadIngredientTypes', 'loadDefaultIngredientTypes', 'createIngredientType']),
    ...mapActions('stocks', ['loadStocks']),

    onTypeSelect ({ type, isDefault }) {
      this.selectedType = type
      this.isDefaultType = isDefault
      this.form.ingredient_type = type

      this.form.name = type.name

      this.createTypeEnabled = true
      this.errors.ingredient_type = null

      this.$nextTick(() => {
        if (this.$refs.nameInput && this.$refs.nameInput.$el) {
          const input = this.$refs.nameInput.$el.querySelector('input')
          if (input) {
            input.focus()
          }
        }
      })
    },

    onCreateTypeEnabled (enabled) {
      this.createTypeEnabled = enabled
      if (!enabled) {
        this.selectedType = null
        this.form.ingredient_type = null
      }
      this.errors.ingredient_type = null
    },

    onCreateNew (typeName) {
      this.form.name = typeName

      this.$nextTick(() => {
        if (this.$refs.nameInput && this.$refs.nameInput.$el) {
          const input = this.$refs.nameInput.$el.querySelector('input')
          if (input) {
            input.focus()
          }
        }
      })
    },

    async onCreate () {
      if (!this.checkValidData()) return

      this.loading = true

      let typeId = null

      if (this.form.ingredient_type && this.form.ingredient_type.id) {
        typeId = this.form.ingredient_type.id
      }

      if (!this.selectedType || this.isDefaultType) {
        this.creatingNewType = true
        const typePayload = {
          farm: +this.$route.params.farm_id,
          name: this.typeSearchText
        }
        const typeError = await this.createIngredientType(typePayload)

        if (typeError) {
          this.$toasted.error(this.$t('errors.incorrect_data'))
          this.loading = false
          this.creatingNewType = false
          return
        }

        const farm = +this.$route.params.farm_id
        await this.loadIngredientTypes({ farm, limit: 10000 })

        const newType = this.ingredientTypes.find(t =>
          t.name.toLowerCase() === this.typeSearchText.toLowerCase()
        )

        if (newType) {
          typeId = newType.id
        } else {
          this.$toasted.error(this.$t('errors.type_not_created'))
          this.loading = false
          this.creatingNewType = false
          return
        }

        this.creatingNewType = false
      }

      const payload = {
        ...this.form,
        ingredient_type: { id: typeId },
        farm: this.$route.params.farm_id
      }

      const { errors } = await this.createIngredient(payload)
      await this.loadIngredients({ farm: this.$route.params.farm_id })

      if (!errors) {
        this.$root.$emit('UpdateFodderTable')
        this.$emit('close')
      } else {
        this.errors = errors

        if (this.errors.non_field_errors) {
          this.errors.ingredient_type = ['5']
        }
      }

      this.loading = false
    },

    checkValidData () {
      let valid = true

      this.errors.name = null
      this.errors.stock = null
      this.errors.ingredient_type = null

      if (!this.form.name) {
        this.errors.name = ['3']
        valid = false
      } else if (this.form.name.length >= 100) {
        this.errors.name = ['6', 100]
        valid = false
      }

      if (!this.form.stock) {
        this.errors.stock = ['3']
        valid = false
      }

      if (!this.typeSearchText) {
        this.errors.ingredient_type = ['3']
        valid = false
      }

      return valid
    },

    onToleranceUpdate (payload) {
      this.form.tolerance_kg = payload.tolerance_kg
      this.form.tolerance_percent = payload.tolerance_percent
      this.form.tolerance_unit = payload.tolerance_unit
    }
  }
}
</script>
<style scoped>
.v-card__title {
  padding: 2rem 2.4rem 1.6rem;
}

.modal-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
}

.v-card__content {
  border-top: .1rem solid #d7d7d7;
  padding-bottom: 1.5rem !important;
}

.v-card__actions {
  padding: 0 2.4rem 2.4rem;
}

.v-card__actions button {
  width: 100%;
}
</style>